import React from 'react';
import { TONE_BY_ZONE, type OverviewTone } from '../overviewMeta';
import { useOverviewMetric } from '../useOverviewMetric';

export type CashCoverageData = {
    tone: OverviewTone;
    cashTetri: number;
    obligationsTetri: number;
    /** Cash ÷ obligations. A multiple, not an amount. */
    ratio: number;
    deltaRatio: number | null;
};

/**
 * Cash coverage: what is in the accounts against what falls due in 30 days.
 *
 * Both sides are required before the card renders a ratio. The server can know
 * the cash half without the obligations half — nothing in the ledger carries a
 * payment due date yet — and a ratio computed against a missing denominator is
 * the most convincing wrong answer this screen could give.
 */
export const useCashCoverage = () => {
    const { metric, error, isLoading, missing, onRetry } =
        useOverviewMetric('cashCoverage');

    const data = React.useMemo<CashCoverageData | null>(() => {
        if (!metric || metric.state !== 'COMPUTED') {
            return null;
        }
        const { tone, cashTetri, obligationsTetri, ratio } = metric;
        if (
            tone === null ||
            cashTetri === null ||
            obligationsTetri === null ||
            ratio === null
        ) {
            return null;
        }
        return {
            tone: TONE_BY_ZONE[tone],
            cashTetri,
            obligationsTetri,
            ratio,
            deltaRatio: metric.deltaRatio,
        };
    }, [metric]);

    // Stub: there is no bank-connection flow anywhere in BOS yet. See
    // `analytics-financial-position-api-spec.md`.
    const onEmptyAction = React.useCallback(() => {}, []);

    return { data, error, isLoading, missing, onRetry, onEmptyAction };
};
